import React from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Box,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import {
  getComplianceMetrics,
  getRiskHeatmap,
  getDomainVulnerability,
  getLiabilityWatch
} from 'services/vendor-dashboard-service';
import { TrustRadar } from 'Components/dashboard/TrustRadar';
import { RiskHeatmap } from 'Components/dashboard/RiskHeatmap';
import { DomainVulnerabilityRadar } from 'Components/dashboard/DomainVulnerabilityRadar';
import { LiabilityWatchList } from 'Components/dashboard/LiabilityWatchList';
import { useAuthTenant } from 'store/auth';

/**
 * DashboardPage Component
 * Executive dashboard with key vendor assessment metrics
 */
export const DashboardPage = () => {
  const { t } = useTranslation(['dashboard']);
  const selectedTenant = useSelector(useAuthTenant);
  const tenant = selectedTenant?.slug;

  // Fetch all dashboard data
  const {
    data: metricsData,
    isLoading: metricsLoading,
    error: metricsError,
    refetch: refetchMetrics
  } = useQuery(
    ['dashboard-metrics', tenant],
    () => getComplianceMetrics(tenant),
    {
      enabled: !!tenant,
      staleTime: 5 * 60 * 1000 // 5 minutes
    }
  );

  const {
    data: heatmapData,
    isLoading: heatmapLoading,
    refetch: refetchHeatmap
  } = useQuery(
    ['dashboard-heatmap', tenant],
    () => getRiskHeatmap(tenant),
    {
      enabled: !!tenant,
      staleTime: 5 * 60 * 1000
    }
  );

  const {
    data: radarData,
    isLoading: radarLoading,
    refetch: refetchRadar
  } = useQuery(
    ['dashboard-radar', tenant],
    () => getDomainVulnerability(tenant),
    {
      enabled: !!tenant,
      staleTime: 5 * 60 * 1000
    }
  );

  const {
    data: liabilityData,
    isLoading: liabilityLoading,
    refetch: refetchLiability
  } = useQuery(
    ['dashboard-liability', tenant],
    () => getLiabilityWatch(tenant),
    {
      enabled: !!tenant,
      staleTime: 5 * 60 * 1000
    }
  );

  const handleRefreshAll = () => {
    refetchMetrics();
    refetchHeatmap();
    refetchRadar();
    refetchLiability();
  };

  // Transform domain vulnerability data to array format expected by component
  const transformedRadarData = React.useMemo(() => {
    const radarResponse = radarData?.data?.success ? radarData.data.data : null;
    if (!radarResponse || !radarResponse.domains) return null;

    return radarResponse.domains.map((domain, index) => ({
      domain,
      score: radarResponse.scores[index] || 0,
      percentage: radarResponse.scores[index] || 0
    }));
  }, [radarData]);

  const isLoading = metricsLoading || heatmapLoading || radarLoading || liabilityLoading;

  if (metricsError) {
    return (
      <Container maxWidth="xl">
        <Box py={4}>
          <Alert severity="error">
            <Typography variant="body1">{t('dashboard:vendor.errorLoad')}</Typography>
            <Typography variant="body2">{metricsError.message}</Typography>
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box py={4}>
        {/* Page Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Box>
            <Typography variant="h4" gutterBottom>
              {t('dashboard:vendor.title')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t('dashboard:vendor.subtitle')}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={isLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={handleRefreshAll}
            disabled={isLoading}
          >
            {t('dashboard:vendor.refreshData')}
          </Button>
        </Box>

        {/* Informational banner when no approved assessments exist */}
        {!metricsLoading && metricsData?.data?.success && metricsData.data.data?.totalVendors === 0 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              {t('dashboard:vendor.approvedOnly')}
            </Typography>
          </Alert>
        )}

        {/* Key Metrics Cards (Top Row) */}
        <Box mb={4}>
          {metricsLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TrustRadar metrics={metricsData?.data?.success ? metricsData.data.data : null} />
          )}
        </Box>

        {/* Charts Row (Middle) */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={6}>
            {heatmapLoading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : (
              <RiskHeatmap data={heatmapData?.data?.success ? heatmapData.data.data : null} />
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            {radarLoading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : (
              <DomainVulnerabilityRadar data={transformedRadarData} />
            )}
          </Grid>
        </Grid>

        {/* Liability Watch List (Bottom) */}
        <Box>
          {liabilityLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <LiabilityWatchList vendors={liabilityData?.data?.success ? liabilityData.data.data?.vendors : null} />
          )}
        </Box>
      </Box>
    </Container>
  );
};
